// FILENAME: frontend/src/components/ComparisonView.jsx
import React from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    resolveApiPresenceStatus,
    resolveEnrollmentLifecycleStatus,
} from '../utils/enrollmentEvidence';
import { getApiPresenceStatusMeta } from '../utils/status';

const ComparisonView = ({ open, onClose, dealers }) => {
    if (!dealers || dealers.length === 0) {
        return null;
    }

    // Create a master list of all unique product selections from all dealers being compared
    const allProducts = {};
    dealers.forEach(dealer => {
        (dealer.productSelections || []).forEach(p => {
            const key = `${p.vendorName || 'unknown'}-${p.serviceItemName || 'unknown'}`;
            if (!allProducts[key]) {
                allProducts[key] = {
                    vendorName: p.vendorName,
                    serviceItemName: p.serviceItemName,
                    dealers: {}
                };
            }
            allProducts[key].dealers[dealer.bac] = p.productSelectionStatus;
        });
    });

    const uniqueProducts = Object.values(allProducts);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
            <DialogTitle>
                Dealer Comparison
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {/* Top Section: Basic Dealer Info */}
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Metric</TableCell>
                                {dealers.map(d => (
                                    <TableCell key={d.id || d.bac} sx={{ fontWeight: 'bold' }}>
                                        {d.dealerName} ({d.bac})
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>Program</TableCell>
                                {dealers.map(d => <TableCell key={d.id || d.bac}>{d.programName}</TableCell>)}
                            </TableRow>
                             <TableRow>
                                <TableCell>Enrollment Status</TableCell>
                                {dealers.map(d => (
                                    <TableCell key={d.id || d.bac}>
                                        {resolveEnrollmentLifecycleStatus(d) || 'Lifecycle Unavailable'}
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow>
                                <TableCell>API Presence</TableCell>
                                {dealers.map(d => (
                                    <TableCell key={d.id || d.bac}>
                                        {getApiPresenceStatusMeta(resolveApiPresenceStatus(d)).label || 'Unknown'}
                                    </TableCell>
                                ))}
                            </TableRow>
                            <TableRow>
                                <TableCell>Has Live Selections</TableCell>
                                {dealers.map(d => <TableCell key={d.id || d.bac}>{d.hasLiveSelections ? 'Yes' : 'No'}</TableCell>)}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Bottom Section: Product Selections */}
                <Typography variant="h6" gutterBottom>Product Selections</Typography>
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        <TableHead>
                             <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                                {dealers.map(d => <TableCell key={d.id || d.bac} sx={{ fontWeight: 'bold' }}>{d.bac}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {uniqueProducts.map((product) => (
                                <TableRow key={`${product.vendorName || 'unknown'}-${product.serviceItemName || 'unknown'}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: (theme) => theme.palette.action.hover } }}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">{product.serviceItemName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{product.vendorName}</Typography>
                                    </TableCell>
                                    {dealers.map(dealer => (
                                        <TableCell key={dealer.id || dealer.bac}>
                                            {product.dealers[dealer.bac] ? (
                                                <Chip label={product.dealers[dealer.bac]} color={product.dealers[dealer.bac] === 'ACTIVE' ? 'success' : 'default'} size="small" />
                                            ) : (
                                                <Typography variant="caption" color="text.disabled">-</Typography>
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};

export default ComparisonView;
